import { Component } from 'react';
import { AlertOctagon, RotateCw, Home } from 'lucide-react';

/**
 * The last line of defence for a render that threw.
 *
 * React unmounts the whole tree when a render error escapes, so without this
 * the console goes blank and the only recovery is the browser's own reload
 * button. Two ways out are offered because they fail differently: reloading
 * fixes a transient error in place, while going back to the root escapes a
 * view that throws every time it mounts.
 *
 * Both are plain browser navigations rather than router calls — the router
 * lives inside the subtree that just failed, and a boundary that depends on
 * the thing it is catching is not a boundary.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack reachable in the console; there is no error sink to ship
    // it to, and swallowing it would make the boundary worse than the crash.
    console.error('Unhandled render error', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="crash">
        <div className="crash__card">
          <div className="crash__icon">
            <AlertOctagon size={20} />
          </div>
          <h1 className="crash__title">Something broke in the console</h1>
          <p className="crash__hint">
            {this.props.scope
              ? `The ${this.props.scope} view stopped rendering. Nothing you sent was lost — the error is in this screen, not in your data.`
              : 'This screen stopped rendering. Nothing you sent was lost.'}
          </p>

          <pre className="crash__detail">{error.message || String(error)}</pre>

          <div className="crash__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => window.location.reload()}
            >
              <RotateCw size={13} /> Reload
            </button>
            <a className="button" href="/">
              <Home size={13} /> Back to the dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
